import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  BarChart,
  CalendarMonth,
  Campaign,
  CheckBox,
  CurrencyRupee,
  FactCheck,
  Groups,
  Paid,
  People,
  PersonAddAlt,
  Refresh,
  School,
  VolumeUp,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { dashboardAPI } from '../../services/api';
import StudentOverviewTable from './StudentOverviewTable';
import {
  Chart as ChartJS,
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  ChartTooltip
);

const palette = {
  ink: '#071637',
  muted: '#52617a',
  line: '#e2e8f2',
  surface: '#ffffff',
  blue: '#2f76ff',
  green: '#2cbf65',
  violet: '#965eea',
  amber: '#f4a611',
  red: '#ef4444',
};

const numberFormat = new Intl.NumberFormat('en-IN');
const toNumber = (value) => Number(value || 0);
const formatNumber = (value) => numberFormat.format(toNumber(value));

const formatDate = (value) => {
  if (!value) return 'Today';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Today';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

const Panel = ({ children, sx }) => (
  <Paper
    elevation={0}
    sx={{
      bgcolor: palette.surface,
      border: `1px solid ${palette.line}`,
      borderRadius: 3,
      boxShadow: '0 18px 46px rgba(7, 22, 55, 0.05)',
      ...sx,
    }}
  >
    {children}
  </Paper>
);

const PanelHeader = ({ title, action }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
    <Typography variant="h6" sx={{ fontSize: 17, color: palette.ink, fontWeight: 800 }}>
      {title}
    </Typography>
    {action}
  </Stack>
);

const StatCard = ({ icon, label, value, trend, color, loading }) => (
  <Panel sx={{ p: 2.5, minHeight: 140 }}>
    <Stack direction="row" spacing={2} alignItems="center">
      <Avatar
        sx={{
          width: 66,
          height: 66,
          bgcolor: `${color}16`,
          color,
          '& svg': { fontSize: 33 },
        }}
      >
        {icon}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" color={palette.muted} fontWeight={600}>
          {label}
        </Typography>
        <Typography variant="h4" color={palette.ink} fontWeight={800} sx={{ mt: 0.5 }}>
          {loading ? '...' : value}
        </Typography>
        <Typography variant="caption" sx={{ color: trend.startsWith('No') ? palette.muted : palette.green, fontWeight: 600 }}>
          {trend}
        </Typography>
      </Box>
    </Stack>
  </Panel>
);

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalTeachers: 0,
    todayAttendance: 0,
    pendingFees: 0,
    classWiseCount: [],
    recentActivities: [],
    genderBreakdown: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await dashboardAPI.getAdminStats();
      setStats((prev) => ({
        ...prev,
        ...(response.data || {}),
        classWiseCount: Array.isArray(response.data?.classWiseCount)
          ? response.data.classWiseCount
          : [],
        recentActivities: Array.isArray(response.data?.recentActivities)
          ? response.data.recentActivities
          : [],
        genderBreakdown: response.data?.genderBreakdown || null,
      }));
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setError(error.response?.data?.message || 'Failed to load dashboard report');
    } finally {
      setLoading(false);
    }
  };

  const classRows = useMemo(() => stats.classWiseCount.map((item) => ({
    standard: item.standard,
    total: toNumber(item.total ?? item.count),
  })), [stats.classWiseCount]);

  const studentTotal = toNumber(stats.totalStudents);
  const teacherTotal = toNumber(stats.totalTeachers);
  const attendance = Math.round(toNumber(stats.todayAttendance));
  const dueCount = toNumber(stats.pendingFees);
  const presentCount = Math.round((studentTotal * attendance) / 100);
  const absentCount = Math.max(studentTotal - presentCount, 0);
  const classCount = classRows.length;
  const clearedFeeCount = Math.max(studentTotal - dueCount, 0);

  const feeTrendData = {
    labels: ['May 1', 'May 5', 'May 10', 'May 15', 'May 20', 'May 25', 'May 31'],
    datasets: [
      {
        label: 'Cleared Students',
        data: [
          Math.round(clearedFeeCount * 0.18),
          Math.round(clearedFeeCount * 0.32),
          Math.round(clearedFeeCount * 0.47),
          Math.round(clearedFeeCount * 0.62),
          Math.round(clearedFeeCount * 0.78),
          Math.round(clearedFeeCount * 0.9),
          clearedFeeCount,
        ],
        borderColor: palette.blue,
        backgroundColor: 'rgba(47, 118, 255, 0.12)',
        pointBackgroundColor: '#fff',
        pointBorderColor: palette.blue,
        pointBorderWidth: 2,
        pointRadius: 3,
        borderWidth: 3,
        fill: true,
        tension: 0.42,
      },
    ],
  };

  const feeTrendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: palette.ink,
        titleColor: '#fff',
        bodyColor: '#dbeafe',
        displayColors: false,
        padding: 12,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#53627d', font: { size: 12, weight: 700 } },
      },
      y: {
        beginAtZero: true,
        border: { display: false },
        grid: { color: '#edf2f7' },
        ticks: { color: '#53627d', precision: 0 },
      },
    },
  };

  const statCards = [
    {
      label: 'Total Students',
      value: formatNumber(studentTotal),
      trend: studentTotal ? '↑ Active admissions' : 'No change',
      icon: <Groups />,
      color: palette.blue,
    },
    {
      label: 'Total Staff',
      value: formatNumber(teacherTotal),
      trend: teacherTotal ? '↑ Active staff' : 'No change',
      icon: <PersonAddAlt />,
      color: palette.green,
    },
    {
      label: 'Total Classes',
      value: formatNumber(classCount),
      trend: 'No change',
      icon: <School />,
      color: palette.violet,
    },
    {
      label: 'Fee Dues',
      value: formatNumber(dueCount),
      trend: dueCount ? 'Needs follow-up' : 'No pending dues',
      icon: <CurrencyRupee />,
      color: palette.amber,
    },
  ];

  const announcements = stats.recentActivities.slice(0, 3);

  const scheduleItems = [
    { time: '08:00 AM', title: 'Morning Assembly', meta: 'School Auditorium', icon: <VolumeUp />, color: palette.blue },
    { time: '09:00 AM', title: 'Attendance Review', meta: `${attendance}% marked today`, icon: <FactCheck />, color: palette.amber },
    { time: '11:00 AM', title: 'Fee Follow-up', meta: `${formatNumber(dueCount)} pending students`, icon: <CurrencyRupee />, color: palette.green },
    { time: '01:30 PM', title: 'Class Reports', meta: `${formatNumber(classCount)} classes active`, icon: <BarChart />, color: palette.violet },
    { time: '03:00 PM', title: 'Notice Review', meta: 'Communications desk', icon: <Campaign />, color: palette.red },
  ];

  const quickActions = [
    { label: 'Add Student', icon: <PersonAddAlt />, path: '/admin/students', color: palette.blue },
    { label: 'Add Staff', icon: <People />, path: '/admin/teachers', color: palette.green },
    { label: 'Create Notice', icon: <Campaign />, path: '/admin/notifications', color: palette.violet },
    { label: 'Mark Attendance', icon: <CheckBox />, path: '/admin/attendance', color: palette.amber },
    { label: 'Collect Fee', icon: <Paid />, path: '/admin/fee-payments', color: palette.green },
    { label: 'Generate Report', icon: <BarChart />, path: '/admin/reports', color: palette.blue },
  ];

  return (
    <Box sx={{ maxWidth: 1500, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2.5 }}>
        <Box>
          <Typography variant="h4" sx={{ color: palette.ink, fontWeight: 800, fontSize: { xs: 30, md: 34 } }}>
            Dashboard
          </Typography>
          <Typography variant="body1" color={palette.muted} sx={{ mt: 0.75 }}>
            Welcome back, {user?.full_name || user?.username || 'Admin'}!
          </Typography>
        </Box>
        <IconButton
          aria-label="Refresh dashboard"
          onClick={loadDashboardData}
          sx={{
            bgcolor: '#fff',
            border: `1px solid ${palette.line}`,
            borderRadius: 2,
            color: palette.blue,
            '&:hover': { bgcolor: '#eef5ff' },
          }}
        >
          <Refresh />
        </IconButton>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2.25} sx={{ mb: 2.25 }}>
        {statCards.map((card) => (
          <Grid item xs={12} sm={6} lg={3} key={card.label}>
            <StatCard {...card} loading={loading} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2.25} sx={{ mb: 2.25 }}>
        <Grid item xs={12} lg={6}>
          <Panel sx={{ p: 2.5, minHeight: 320 }}>
            <PanelHeader
              title="Student Attendance Overview"
              action={
                <Button size="small" endIcon={<CalendarMonth />} sx={{ textTransform: 'none', fontWeight: 600 }}>
                  This Month
                </Button>
              }
            />

            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={5}>
                <Box
                  sx={{
                    width: 206,
                    height: 206,
                    mx: 'auto',
                    borderRadius: '50%',
                    background: `conic-gradient(${palette.green} 0 ${attendance}%, ${palette.red} ${attendance}% ${Math.min(attendance + 5, 100)}%, ${palette.amber} ${Math.min(attendance + 5, 100)}% 100%)`,
                    display: 'grid',
                    placeItems: 'center',
                    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.03)',
                  }}
                >
                  <Box
                    sx={{
                      width: 132,
                      height: 132,
                      borderRadius: '50%',
                      bgcolor: '#fff',
                      display: 'grid',
                      placeItems: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <Box>
                      <Typography variant="h4" color={palette.ink} fontWeight={800}>
                        {loading ? '...' : `${attendance}%`}
                      </Typography>
                      <Typography variant="body2" color={palette.muted} fontWeight={700}>
                        Overall
                      </Typography>
                      <Typography variant="body2" color={palette.muted} fontWeight={700}>
                        Attendance
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={7}>
                <Stack spacing={2.2}>
                  {[
                    { label: 'Present', value: `${formatNumber(presentCount)} (${attendance}%)`, color: palette.green },
                    { label: 'Absent', value: `${formatNumber(absentCount)} (${Math.max(100 - attendance, 0)}%)`, color: palette.red },
                    { label: 'Leave', value: '0 (0%)', color: palette.amber },
                  ].map((item) => (
                    <Stack key={item.label} direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: item.color }} />
                        <Typography variant="body2" color={palette.ink} fontWeight={600}>
                          {item.label}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color={palette.muted} fontWeight={600}>
                        {loading ? '...' : item.value}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </Panel>
        </Grid>

        <StudentOverviewTable></StudentOverviewTable>
      </Grid>

      <Grid container spacing={2.25}>
        <Grid item xs={12} lg={4}>
          <Panel sx={{ p: 2.5, height: '100%' }}>
            <PanelHeader
              title="Recent Announcements"
              action={
                <Button size="small" onClick={() => navigate('/admin/notifications')} sx={{ textTransform: 'none', fontWeight: 700 }}>
                  View All
                </Button>
              }
            />
            <Stack spacing={2}>
              {announcements.length > 0 ? announcements.map((activity, index) => (
                <Stack key={`${activity.activity_date}-${index}`} direction="row" spacing={1.75}>
                  <Avatar
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 3,
                      bgcolor: index === 0 ? '#eaf3ff' : index === 1 ? '#e8f8ee' : '#fff2d9',
                      color: index === 0 ? palette.blue : index === 1 ? palette.green : palette.amber,
                    }}
                  >
                    {index === 0 ? <VolumeUp /> : index === 1 ? <CalendarMonth /> : <Campaign />}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} color={palette.ink} noWrap>
                      {activity.activity || 'Dashboard update'}
                    </Typography>
                    <Typography variant="body2" color={palette.muted} sx={{ mt: 0.35 }} noWrap>
                      {activity.student_name || 'School activity'}
                    </Typography>
                    <Typography variant="caption" color={palette.muted} fontWeight={600}>
                      {formatDate(activity.activity_date)} · Admin
                    </Typography>
                  </Box>
                </Stack>
              )) : (
                <Typography variant="body2" color={palette.muted}>
                  No recent announcements.
                </Typography>
              )}
            </Stack>
          </Panel>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Panel sx={{ p: 2.5, height: '100%' }}>
            <PanelHeader
              title="Today's Schedule"
              action={
                <Button size="small" onClick={() => navigate('/admin/academic-years')} sx={{ textTransform: 'none', fontWeight: 700 }}>
                  View Timetable
                </Button>
              }
            />
            <Stack spacing={1.4}>
              {scheduleItems.map((item) => (
                <Stack key={item.time} direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="body2" color={palette.muted} fontWeight={600} sx={{ width: 72 }}>
                    {item.time}
                  </Typography>
                  <Avatar sx={{ width: 34, height: 34, bgcolor: `${item.color}18`, color: item.color, borderRadius: 1.5 }}>
                    {item.icon}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1, pb: 1.2, borderBottom: `1px solid ${palette.line}` }}>
                    <Typography variant="body2" color={palette.ink} fontWeight={700} noWrap>
                      {item.title}
                    </Typography>
                    <Typography variant="caption" color={palette.muted} fontWeight={700} noWrap component="div">
                      {loading ? 'Loading...' : item.meta}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Panel>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Panel sx={{ p: 2.5, height: '100%' }}>
            <PanelHeader title="Quick Actions" />
            <Grid container spacing={1.5}>
              {quickActions.map((action) => (
                <Grid item xs={6} key={action.label}>
                  <Button
                    fullWidth
                    onClick={() => navigate(action.path)}
                    sx={{
                      height: 108,
                      border: `1px solid ${palette.line}`,
                      borderRadius: 2,
                      bgcolor: '#fff',
                      color: palette.ink,
                      textTransform: 'none',
                      flexDirection: 'column',
                      gap: 1,
                      fontWeight: 700,
                      '&:hover': {
                        borderColor: action.color,
                        bgcolor: `${action.color}0f`,
                      },
                    }}
                  >
                    <Box sx={{ color: action.color, '& svg': { fontSize: 32 } }}>
                      {action.icon}
                    </Box>
                    {action.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Panel>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
